'use client';

import { Link, usePathname } from '@/i18n/navigation';
import { StudioMenuLabelEnum } from '@/types';
import { useTranslations } from 'next-intl';
import { NAVIGATION_ITEM_CLASS, NAVIGATION_LAST_ITEM_CLASS, NavigationDropDown } from '../navigation-dropdown';
import type { StudioDropDownProps, StudioMenuItem, StudioRoute } from './interfaces';

const SELLER_MENU_ITEMS: ReadonlyArray<{ id: StudioMenuLabelEnum; translationKey: string; href: StudioRoute }> = [
    { id: StudioMenuLabelEnum.DASHBOARD, translationKey: 'dashboard.menuTitle', href: '/studio/dashboard' },
    { id: StudioMenuLabelEnum.MY_PROGRAMS, translationKey: 'programs', href: '/studio/programs' },
    { id: StudioMenuLabelEnum.MY_FORMATS, translationKey: 'formats', href: '/studio/formats' },
    { id: StudioMenuLabelEnum.MY_LICENCES, translationKey: 'licences', href: '/studio/licenses' },
];

const BUYER_MENU_ITEMS: ReadonlyArray<{ id: StudioMenuLabelEnum; translationKey: string; href: StudioRoute }> = [
    { id: StudioMenuLabelEnum.MY_LICENCES, translationKey: 'licences', href: '/studio/licenses' },
];

export const StudioDropDown = ({ isSeller, callback }: StudioDropDownProps) => {
    const t = useTranslations('Navigation.sellerDashboard');
    const pathname = usePathname();
    const activeMenuItemClass =
        'whitespace-nowrap flex items-center gap-2 rounded-[8px] p-[10px] my-2 relative w-full text-left text-base bg-[#DD06AE]';

    const getActiveMenuFromPath = (): StudioMenuLabelEnum | undefined => {
        if (pathname.includes('/studio/dashboard')) return StudioMenuLabelEnum.DASHBOARD;
        if (pathname.includes('/studio/programs')) return StudioMenuLabelEnum.MY_PROGRAMS;
        if (pathname.includes('/studio/formats')) return StudioMenuLabelEnum.MY_FORMATS;
        if (pathname.includes('/studio/licenses')) return StudioMenuLabelEnum.MY_LICENCES;
        return undefined;
    };

    const activeMenuItem = getActiveMenuFromPath();

    const menuItemConfigs = isSeller ? SELLER_MENU_ITEMS : BUYER_MENU_ITEMS;

    const translateDynamicKey = t as (key: string) => string;

    const menuItems: StudioMenuItem[] = menuItemConfigs.map((item) => ({
        id: item.id,
        label: translateDynamicKey(item.translationKey),
        href: item.href,
    }));

    return (
        <NavigationDropDown
            label={t('studio')}
            isActive={!!activeMenuItem}
            panelClassName="max-h-[440px] sm:max-h-[initial] overflow-y-auto"
        >
            {(close) =>
                menuItems.map((item, index, array) => (
                    <li key={item.id}>
                        <Link
                            className={`${activeMenuItem === item.id ? activeMenuItemClass : `whitespace-nowrap ${NAVIGATION_ITEM_CLASS}`} ${index === array.length - 1 ? NAVIGATION_LAST_ITEM_CLASS : ''}`}
                            onClick={() => {
                                callback();
                                close();
                            }}
                            href={item.href}
                        >
                            <span>{item.label}</span>
                        </Link>
                    </li>
                ))
            }
        </NavigationDropDown>
    );
};
